import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  User,
  Building2,
  Stethoscope,
  Building,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Check,
  AlertTriangle,
  Loader2,
  X,
  Database,
  FileSpreadsheet,
  UploadCloud,
  Sparkles,
  History,
  Tag,
  Wand2,
  Pencil,
  Trash2,
} from "lucide-react";
import type { EntityType, MdmFieldMapping, MdmRun } from "../types";
import {
  createRun,
  registerSourceFromConnector,
  waitForSourceRegistration,
  normalizeRun,
  waitForNormalization,
  getFields,
  getConnectors,
  uploadExcelSource,
  listRuns,
  getRunStatus,
  deleteConnector,
  getConnectionPresets,
  createSampleRun,
  profileConnector,
  suggestFieldMappings,
  updateFieldMappings,
  applyEntityTemplate,
  getEntityTypes,
} from "../api/mdm-client";
import { useMdmRunStore } from "../store/useMdmRunStore";
import DatabaseConnect from "../../../components/DatabaseConnect";
import { CONNECTION_PRESETS } from "../config/connectionPresets";
import type { ConnectionPreset } from "../config/connectionPresets";
import {
  ConnectDatabaseDialog,
  type ConnectResult,
  type DbConnectFormValues,
} from "../../../components/data-source/ConnectDatabaseDialog";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ConnectorCard {
  id: string;
  name: string;
  alias?: string;
  table_or_query?: string;
  record_count: number;
  last_sync_date: string;
}

interface Props {
  projectId: string;
  onComplete: (runId: string, entityType: EntityType) => void;
  onBack: () => void;
  onViewGoldenRecords?: (runId: string) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ENTITY_TYPES: {
  type: EntityType;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  description: string;
}[] = [
  {
    type: "person",
    label: "Person",
    icon: User,
    description: "Individuals, customers, employees",
  },
  {
    type: "organization",
    label: "Organization",
    icon: Building2,
    description: "Companies, accounts, partners",
  },
  {
    type: "provider",
    label: "Provider",
    icon: Stethoscope,
    description: "Healthcare providers, NPI records",
  },
  {
    type: "facility",
    label: "Facility",
    icon: Building,
    description: "Locations, sites, campuses",
  },
];

// P1: New step labels — entity type moves to Step 3
const STEP_LABELS = ["Configure", "Source", "Map"];

// Brand green palette — matches --primary: #86bc25
const G = {
  bg: "bg-[#86bc25]",
  bgFaint: "bg-[#86bc25]/10",
  bgHover: "hover:bg-[#86bc25]/15",
  border: "border-[#86bc25]/40",
  borderHover: "hover:border-[#86bc25]/50",
  text: "text-[#86bc25]",
  ring: "focus:ring-[#86bc25]/20 focus:border-[#86bc25]/40",
} as const;

// Friendly run name suggestions
function suggestRunName(): string {
  const now = new Date();
  const mon = now.toLocaleString("en-US", { month: "short" }).toLowerCase();
  const day = now.getDate();
  return `entity-dedup-${mon}-${day}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEP_LABELS.map((label, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <div key={step} className="flex items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-200 border ${
                  done
                    ? `${G.bg} border-[#86bc25]/40 text-[#0e0e18]`
                    : active
                      ? `${G.bgFaint} ${G.border} ${G.text}`
                      : "bg-white/[0.06] border-[var(--border-muted)] text-[var(--text-primary)]"
                }`}
              >
                {done ? <Check size={14} /> : step}
              </div>
              <span
                className={`text-[0.6875rem] tracking-widest uppercase ${
                  active
                    ? G.text
                    : done
                      ? "text-[var(--text-primary)]"
                      : "text-[var(--text-primary)]"
                }`}
              >
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div
                className={`h-px mx-3 mb-5 w-10 ${done ? "bg-[#86bc25]/40" : "bg-white/[0.08]"}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ErrorBanner({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss: () => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl px-4 py-3 mb-4 bg-rose-500/10 border border-rose-500/25">
      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-rose-400" />
      <p className="text-sm flex-1 text-rose-300">{message}</p>
      <button
        onClick={onDismiss}
        className="opacity-50 hover:opacity-100 transition-opacity text-rose-300"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ─── Past Runs Panel ──────────────────────────────────────────────────────────

function PastRunsPanel({
  projectId,
  onNavigate,
}: {
  projectId: string;
  onNavigate: (runId: string, name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState<MdmRun[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listRuns(projectId)
      .then((r) =>
        setRuns(r.items.filter((x) => x.status === "completed").slice(0, 8)),
      )
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-[var(--border-muted)] bg-white/[0.03] ${G.text} ${G.bgHover} ${G.borderHover} transition-all duration-150`}
      >
        <History size={13} />
        View past golden records
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-white/[0.08] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <History size={13} className={G.text} />
          <span className="text-xs font-medium uppercase tracking-widest text-[var(--text-primary)]">
            Past Completed Runs
          </span>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="text-[var(--text-primary)] hover:text-[var(--text-primary)] transition-colors"
        >
          <X size={13} />
        </button>
      </div>
      <div className="px-4 py-3">
        {loading ? (
          <div className="flex items-center gap-2 py-2">
            <Loader2 size={13} className={`animate-spin ${G.text}`} />
            <span className="text-xs text-[var(--text-primary)]">Loading…</span>
          </div>
        ) : runs.length === 0 ? (
          <p className="text-xs text-[var(--text-primary)] py-2">
            No completed runs yet.
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {runs.map((r) => (
              <button
                key={r.id}
                onClick={() => onNavigate(r.id, r.name)}
                className={`flex items-center justify-between px-3 py-2 rounded-lg text-left transition-all border border-white/[0.06] bg-white/[0.03] hover:border-[#86bc25]/30 ${G.bgHover} group`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-medium text-slate-300 truncate">
                    {r.name}
                  </span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-white/[0.07] text-[var(--text-primary)] shrink-0 capitalize">
                    {r.entity_type}
                  </span>
                </div>
                <span
                  className={`text-xs shrink-0 ml-3 ${G.text} opacity-0 group-hover:opacity-100 transition-opacity`}
                >
                  View →
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 1: Run Name only (P1: entity type moved to Step 3) ─────────────────

function Step1({
  runName,
  onNameChange,
  projectId,
  onViewPastRun,
}: {
  runName: string;
  onNameChange: (v: string) => void;
  projectId: string;
  onViewPastRun: (runId: string, name: string) => void;
}) {
  return (
    <div className="flex flex-col gap-6">
      {/* Run name with auto-suggest */}
      <div>
        <label className="block text-xs font-medium mb-2 uppercase tracking-widest text-[var(--text-primary)]">
          Run Name / ID
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={runName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. customer-dedup-jul-14"
            data-testid="mdm-run-name"
            className={`flex-1 rounded-xl px-4 py-3 text-sm outline-none transition-all duration-200 bg-[var(--overlay-subtle)] border border-[var(--border-muted)] text-[var(--text-primary)] placeholder:text-[var(--text-primary)] ${G.ring}`}
          />
          <button
            type="button"
            onClick={() => onNameChange(suggestRunName())}
            title="Auto-generate a friendly run name"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs border border-[var(--border-muted)] bg-white/[0.04] ${G.text} ${G.bgHover} ${G.borderHover} transition-all shrink-0`}
          >
            <Wand2 size={13} />
            Suggest
          </button>
        </div>
        <p className="text-[11px] text-[var(--text-primary)] mt-1.5 ml-1">
          Used as a human-readable label for this resolution run.
        </p>
      </div>

      {/* Past golden records */}
      <PastRunsPanel projectId={projectId} onNavigate={onViewPastRun} />
    </div>
  );
}

// ─── Alias persistence (localStorage) ────────────────────────────────────────

const ALIAS_STORAGE_KEY = "ztd:mdm:connector-aliases";

function loadAliases(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(ALIAS_STORAGE_KEY) ?? "{}");
  } catch {
    return {};
  }
}

function saveAliases(aliases: Record<string, string>) {
  localStorage.setItem(ALIAS_STORAGE_KEY, JSON.stringify(aliases));
}

function useConnectorAliases() {
  const [aliases, setAliases] = useState<Record<string, string>>(loadAliases);

  const setAlias = useCallback((id: string, alias: string) => {
    setAliases((prev) => {
      const next = { ...prev, [id]: alias.trim() };
      saveAliases(next);
      return next;
    });
  }, []);

  const clearAlias = useCallback((id: string) => {
    setAliases((prev) => {
      const next = { ...prev };
      delete next[id];
      saveAliases(next);
      return next;
    });
  }, []);

  return { aliases, setAlias, clearAlias };
}

// ─── ConnectorCardItem ─────────────────────────────────────────────────────────
// P6: Uses radio indicator (filled circle) instead of checkbox — single-source.

function ConnectorCardItem({
  connector,
  active,
  isExcel,
  alias,
  onSelect,
  onAliasChange,
  onRemove,
  fmt,
  fmtDate,
}: {
  connector: ConnectorCard;
  active: boolean;
  isExcel: boolean;
  alias: string;
  onSelect: () => void;
  onAliasChange: (alias: string) => void;
  onRemove: () => Promise<void>;
  fmt: (n: number) => string;
  fmtDate: (s: string) => string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(alias);
  const [removing, setRemoving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(alias);
  }, [alias, editing]);

  function startEdit(e: React.MouseEvent) {
    e.stopPropagation();
    setDraft(alias || connector.name);
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }

  function commitEdit(e?: React.FormEvent) {
    e?.preventDefault();
    const trimmed = draft.trim();
    onAliasChange(trimmed === connector.name || trimmed === "" ? "" : trimmed);
    setEditing(false);
  }

  function cancelEdit(e: React.MouseEvent) {
    e.stopPropagation();
    setDraft(alias);
    setEditing(false);
  }

  async function handleRemove(e: React.MouseEvent) {
    e.stopPropagation();
    if (removing) return;
    setRemoving(true);
    try {
      await onRemove();
    } catch {
      setRemoving(false);
    }
  }

  const displayName = alias || connector.name;

  return (
    <div
      data-testid="mdm-connector-card"
      data-name={connector.name}
      data-active={active}
      data-is-excel={isExcel}
      className={`group flex flex-col gap-2 rounded-xl px-4 py-3.5 text-left transition-all duration-150 border ${
        active
          ? `${G.bgFaint} ${G.border}`
          : "bg-white/[0.04] border-white/[0.08] hover:border-[var(--border-strong)]"
      }`}
    >
      {/* Top row: icon + name/alias editor + radio */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {/* Icon */}
          <div
            className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${
              active ? G.bgFaint : "bg-white/[0.07]"
            }`}
          >
            {isExcel ? (
              <FileSpreadsheet
                size={13}
                className={active ? G.text : "text-emerald-500"}
              />
            ) : (
              <Database
                size={13}
                className={active ? G.text : "text-[var(--text-primary)]"}
              />
            )}
          </div>

          {/* Name / alias editor */}
          <div className="flex-1 min-w-0">
            {editing ? (
              <form
                onSubmit={commitEdit}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1"
              >
                <input
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setDraft(alias);
                      setEditing(false);
                    }
                  }}
                  autoFocus
                  className={`flex-1 min-w-0 text-sm rounded-lg px-2 py-0.5 outline-none bg-[var(--overlay-strong)] border ${G.border} text-[var(--text-primary)] focus:ring-1 ${G.ring}`}
                  placeholder={connector.name}
                />
                <button
                  type="submit"
                  className={`shrink-0 px-2 py-0.5 rounded text-[11px] font-medium ${G.bg} text-[#0e0e18]`}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="shrink-0 px-2 py-0.5 rounded text-[11px] text-[var(--text-primary)] hover:text-slate-300 border border-[var(--border-muted)]"
                >
                  Cancel
                </button>
              </form>
            ) : (
              <div className="flex items-center gap-1.5 group/name">
                <span
                  className={`text-sm font-medium truncate ${active ? "text-[var(--text-primary)]" : "text-slate-200"}`}
                  title={displayName}
                >
                  {displayName}
                </span>
                {alias && alias !== connector.name && (
                  <span
                    className={`text-[11px] px-1.5 py-0.5 rounded-full shrink-0 ${G.bgFaint} ${G.text} border ${G.border} font-medium`}
                  >
                    alias
                  </span>
                )}
                <button
                  onClick={startEdit}
                  className="shrink-0 opacity-0 group-hover/name:opacity-100 transition-opacity text-[var(--text-primary)] hover:text-slate-300"
                  title={alias ? "Edit alias" : "Set alias"}
                >
                  <Pencil size={11} />
                </button>
              </div>
            )}

            {alias && alias !== connector.name && !editing && (
              <span className="text-[11px] text-[var(--text-primary)] flex items-center gap-0.5 mt-0.5">
                <Tag size={8} />
                {connector.name}
              </span>
            )}

            {!isExcel && connector.table_or_query && !editing && (
              <span className="text-xs text-[var(--text-primary)] truncate block">
                {connector.table_or_query}
              </span>
            )}
          </div>
        </div>

        {/* File badge + remove + radio indicator (P6) */}
        <div className="flex items-center gap-2 shrink-0 mt-0.5">
          {isExcel && (
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 uppercase tracking-wider">
              file
            </span>
          )}
          <button
            onClick={handleRemove}
            disabled={removing}
            title="Remove source"
            className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-[var(--text-primary)] hover:text-rose-400 disabled:opacity-50"
          >
            {removing ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Trash2 size={12} />
            )}
          </button>
          {/* P6: Radio indicator — filled circle when active */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            data-testid="mdm-connector-toggle"
            aria-label={active ? "Selected" : "Select this source"}
            className={`w-4 h-4 rounded-full flex items-center justify-center border transition-colors ${
              active
                ? `${G.bg} border-transparent`
                : "bg-white/[0.08] border-[var(--border-muted)] hover:border-[var(--border-strong)]"
            }`}
          >
            {active && (
              <div className="w-1.5 h-1.5 rounded-full bg-[#0e0e18]" />
            )}
          </button>
        </div>
      </div>

      {/* Bottom row: record count + sync date — click whole card to select */}
      <button
        onClick={onSelect}
        className="flex items-center justify-between mt-0.5 w-full text-left"
        tabIndex={-1}
      >
        <span className="text-xs tabular-nums text-[var(--text-primary)]">
          {fmt(connector.record_count)} records
        </span>
        <span className="text-xs text-[var(--text-primary)]">
          {isExcel
            ? "Uploaded file"
            : `Synced ${fmtDate(connector.last_sync_date)}`}
        </span>
      </button>
    </div>
  );
}

// ─── Excel Upload Zone ────────────────────────────────────────────────────────

function ExcelUploadZone({
  projectId,
  onUploaded,
}: {
  projectId: string;
  onUploaded: (card: ConnectorCard) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function handleFile(file: File) {
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !["xlsx", "xls", "csv"].includes(ext)) {
      setUploadError("Only .xlsx, .xls, and .csv files are supported.");
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const card = await uploadExcelSource(file, projectId);
      onUploaded(card);
    } catch (e: unknown) {
      setUploadError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  return (
    <div className="flex flex-col gap-2">
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center gap-2 rounded-xl px-4 py-6 border-2 border-dashed cursor-pointer transition-all duration-150 ${
          dragOver
            ? `border-[#86bc25]/60 bg-[#86bc25]/10`
            : "border-[var(--border-muted)] bg-white/[0.02] hover:border-[var(--border-strong)] hover:bg-white/[0.04]"
        }`}
      >
        <input
          type="file"
          data-testid="mdm-excel-upload-input"
          accept=".xlsx,.xls,.csv"
          className="sr-only"
          onChange={onInputChange}
          disabled={uploading}
        />
        {uploading ? (
          <>
            <Loader2 size={22} className={`animate-spin ${G.text}`} />
            <span className="text-xs text-[var(--text-primary)]">
              Uploading…
            </span>
          </>
        ) : (
          <>
            <UploadCloud
              size={22}
              className={dragOver ? G.text : "text-[var(--text-primary)]"}
            />
            <div className="text-center">
              <p className="text-sm font-medium text-slate-300">
                Drop a file or click to browse
              </p>
              <p className="text-xs text-[var(--text-primary)] mt-0.5">
                .xlsx · .xls · .csv
              </p>
            </div>
          </>
        )}
      </label>
      {uploadError && (
        <div className="flex items-center gap-2 text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
          <AlertTriangle size={12} className="shrink-0" />
          {uploadError}
        </div>
      )}
    </div>
  );
}

// ─── Step 2: Single-source picker (P6) ───────────────────────────────────────

function Step2({
  projectId,
  connectors,
  selected,
  loading,
  onSelect,
  excelSources,
  onExcelUploaded,
  onConnectDatabase,
  onConnectPreset,
  aliases,
  onAliasChange,
  onRemove,
  apiPresets = [],
}: {
  projectId: string;
  connectors: ConnectorCard[];
  selected: string | null; // P6: single selection
  loading: boolean;
  onSelect: (id: string) => void; // P6: radio-style select
  excelSources: ConnectorCard[];
  onExcelUploaded: (card: ConnectorCard) => void;
  onConnectDatabase: () => void;
  onConnectPreset: (preset: ConnectionPreset) => void;
  aliases: Record<string, string>;
  onAliasChange: (id: string, alias: string) => void;
  onRemove: (id: string) => Promise<void>;
  apiPresets?: ConnectionPreset[];
}) {
  const [presetIdx, setPresetIdx] = useState<number>(-1);
  const fmt = (n: number) => n.toLocaleString();
  const fmtDate = (s: string) => {
    try {
      return new Date(s).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return s;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Loader2 size={24} className={`animate-spin ${G.text}`} />
        <p className="text-sm text-[var(--text-primary)]">
          Loading connections…
        </p>
      </div>
    );
  }

  // An upload is attached to the project, so a reload lists it among the
  // project's connectors too: show each card once.
  const allSources = [
    ...connectors,
    ...excelSources.filter((e) => !connectors.some((c) => c.id === e.id)),
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* YAML / saved preset picker */}
      {CONNECTION_PRESETS.length > 0 && (
        <div className={`rounded-xl px-4 py-3 border ${G.border} ${G.bgFaint}`}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={13} className={G.text} />
            <span
              className={`text-xs font-medium uppercase tracking-widest ${G.text}`}
            >
              Quick Connect from saved
            </span>
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <select
                className={`w-full rounded-lg px-3 py-2 text-sm outline-none bg-white/[0.07] border border-[var(--border-muted)] text-slate-200 appearance-none ${G.ring}`}
                value={presetIdx}
                onChange={(e) => setPresetIdx(Number(e.target.value))}
              >
                <option value={-1}>Pick a saved connection…</option>
                {[...apiPresets, ...CONNECTION_PRESETS].map((p, i) => (
                  <option key={i} value={i}>
                    {p.alias}
                    {p.entity_type_hint ? ` [${p.entity_type_hint}]` : ""}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={13}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-primary)] pointer-events-none"
              />
            </div>
            <button
              onClick={() => {
                if (presetIdx >= 0)
                  onConnectPreset(
                    [...apiPresets, ...CONNECTION_PRESETS][presetIdx],
                  );
              }}
              disabled={presetIdx < 0}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                presetIdx >= 0
                  ? `${G.bg} text-[#0e0e18] hover:opacity-90`
                  : "bg-white/[0.07] text-[var(--text-primary)] cursor-not-allowed"
              }`}
            >
              Connect
            </button>
          </div>
        </div>
      )}

      {/* Header actions */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--text-primary)]">
          {allSources.length > 0
            ? `${allSources.length} connection${allSources.length !== 1 ? "s" : ""} available — select one`
            : "No connections configured yet"}
        </p>
        <button
          onClick={onConnectDatabase}
          data-testid="mdm-add-database"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-[var(--border-muted)] bg-white/[0.04] ${G.bgHover} ${G.borderHover} text-[var(--text-primary)] hover:text-[var(--text-primary)] transition-all`}
        >
          <Database size={12} />
          Add Database
        </button>
      </div>

      {/* No selection prompt */}
      {!selected && allSources.length > 0 && (
        <div className="flex items-center gap-2 rounded-xl px-4 py-3 bg-amber-500/10 border border-amber-500/25">
          <AlertTriangle size={14} className="text-amber-400" />
          <p className="text-xs text-amber-300">Select a source to continue.</p>
        </div>
      )}

      {/* Connection cards */}
      {allSources.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <Database size={32} className="text-slate-700" />
          <p className="text-sm text-[var(--text-primary)]">
            No connections configured for this project.
          </p>
          <button
            onClick={onConnectDatabase}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ${G.bg} text-[#0e0e18] hover:opacity-90 transition-opacity`}
          >
            <Database size={14} />
            Connect a Database
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {allSources.map((c) => (
            <ConnectorCardItem
              key={c.id}
              connector={c}
              active={selected === c.id}
              isExcel={excelSources.some((e) => e.id === c.id)}
              alias={aliases[c.id] ?? c.alias ?? ""}
              onSelect={() => onSelect(c.id)}
              onAliasChange={(a) => onAliasChange(c.id, a)}
              onRemove={() => onRemove(c.id)}
              fmt={fmt}
              fmtDate={fmtDate}
            />
          ))}
        </div>
      )}

      {/* Excel / CSV upload */}
      <div>
        <p className="text-xs font-medium uppercase tracking-widest text-[var(--text-primary)] mb-2">
          Upload Excel or CSV
        </p>
        <ExcelUploadZone
          projectId={projectId}
          onUploaded={(card) => {
            onExcelUploaded(card);
            onSelect(card.id);
          }}
        />
      </div>
    </div>
  );
}

// ─── Pre-intake Validation Panel ──────────────────────────────────────────────

function PreIntakePanel({
  connectors,
  selected,
  fields,
  aliases = {},
}: {
  connectors: ConnectorCard[];
  selected: string | null; // P6: single ID
  fields: MdmFieldMapping[];
  aliases?: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const selectedConnector = selected
    ? connectors.find((c) => c.id === selected)
    : null;

  const nullBySource: Record<string, { total: number; sum: number }> = {};
  fields.forEach((f) => {
    if (!nullBySource[f.source_system])
      nullBySource[f.source_system] = { total: 0, sum: 0 };
    nullBySource[f.source_system].total++;
    nullBySource[f.source_system].sum += f.null_rate;
  });

  return (
    <div className="rounded-xl overflow-hidden mt-2 border border-white/[0.08]">
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-[var(--overlay-subtle)]"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="text-xs font-medium uppercase tracking-widest text-[var(--text-primary)]">
          Pre-intake Validation
        </span>
        <div className="flex items-center gap-2">
          {selectedConnector && (
            <span className="text-xs tabular-nums px-2 py-0.5 rounded-md bg-white/[0.07] text-[var(--text-primary)]">
              1 source
            </span>
          )}
          {open ? (
            <ChevronUp size={14} className="text-[var(--text-primary)]" />
          ) : (
            <ChevronDown size={14} className="text-[var(--text-primary)]" />
          )}
        </div>
      </button>

      {open && (
        <div className="border-t border-white/[0.06] px-4 py-4">
          {!selectedConnector ? (
            <p className="text-xs text-[var(--text-primary)]">
              No source selected yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-xs"
                style={{ borderCollapse: "collapse" }}
              >
                <thead>
                  <tr>
                    {["Source", "Records", "Avg Null Rate", "Fields"].map(
                      (h) => (
                        <th
                          key={h}
                          className="text-left pb-2 pr-6 text-[var(--text-primary)] font-medium tracking-wide"
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {[selectedConnector].map((c) => {
                    const ns = nullBySource[c.name];
                    const avgNull = ns ? (ns.sum / ns.total) * 100 : 0;
                    const fieldCount = ns?.total ?? 0;
                    const displayName = aliases[c.id] || c.alias || c.name;
                    return (
                      <tr key={c.id}>
                        <td className="pb-2 pr-6 font-medium text-[var(--text-primary)]">
                          {displayName}
                          {aliases[c.id] && aliases[c.id] !== c.name && (
                            <span className="ml-1.5 text-[var(--text-primary)] font-normal text-xs">
                              ({c.name})
                            </span>
                          )}
                        </td>
                        <td className="pb-2 pr-6 tabular-nums text-[var(--text-primary)]">
                          {c.record_count.toLocaleString()}
                        </td>
                        <td
                          className={`pb-2 pr-6 tabular-nums ${
                            avgNull > 20
                              ? "text-amber-400"
                              : avgNull > 5
                                ? "text-[var(--text-primary)]"
                                : "text-emerald-400"
                          }`}
                        >
                          {fieldCount > 0 ? `${avgNull.toFixed(1)}%` : "—"}
                        </td>
                        <td className="pb-2 tabular-nums text-[var(--text-primary)]">
                          {fieldCount > 0 ? fieldCount : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Entity Type Picker (P1: moved from Step 1 to Step 3) ────────────────────

function EntityTypePicker({
  value,
  onChange,
}: {
  value: EntityType | null;
  onChange: (v: EntityType) => void;
}) {
  return (
    <div className="mb-5">
      <label className="block text-xs font-medium mb-3 uppercase tracking-widest text-[var(--text-primary)]">
        Entity Type — what records represent
      </label>
      <div className="grid grid-cols-2 gap-3">
        {ENTITY_TYPES.map(({ type, label, icon: Icon, description }) => {
          const selected = value === type;
          return (
            <button
              key={type}
              onClick={() => onChange(type)}
              data-testid="mdm-entity-type"
              data-value={type}
              className={`flex items-start gap-3 rounded-xl px-4 py-4 text-left transition-all duration-150 border ${
                selected
                  ? `${G.bgFaint} ${G.border} shadow-[0_0_0_1px_rgba(134,188,37,0.3)]`
                  : "bg-white/[0.04] border-white/[0.08] hover:border-[var(--border-strong)]"
              }`}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                  selected
                    ? `${G.bgFaint} ${G.text}`
                    : "bg-white/[0.07] text-[var(--text-primary)]"
                }`}
              >
                <Icon size={16} />
              </div>
              <div>
                <div
                  className={`text-sm font-medium ${selected ? "text-[var(--text-primary)]" : "text-slate-200"}`}
                >
                  {label}
                </div>
                <div className="text-xs mt-0.5 text-[var(--text-primary)]">
                  {description}
                </div>
              </div>
              {selected && (
                <div className="ml-auto shrink-0">
                  <Check size={14} className={G.text} />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Canonical Field Coverage (P3: accepts canonicalFields prop) ──────────────

function CanonicalCoverage({
  entityType,
  canonicalFields,
  fields,
  excluded,
  sourceColumns,
  onManualMap,
}: {
  entityType: EntityType;
  canonicalFields: string[]; // P3: from API, not static map
  fields: MdmFieldMapping[];
  excluded: Set<string>;
  sourceColumns?: string[]; // full source schema — lets a missing field be mapped by hand
  onManualMap?: (canonical: string, source: string) => void;
}) {
  if (canonicalFields.length === 0) return null;

  const discovered = new Set(
    fields
      .filter((f) => !excluded.has(f.canonical_field))
      .map((f) => f.canonical_field),
  );
  const covered = canonicalFields.filter((cf) => discovered.has(cf));
  const missing = canonicalFields.filter((cf) => !discovered.has(cf));
  const pct = Math.round((covered.length / canonicalFields.length) * 100);

  const barColor =
    pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-400" : "bg-rose-500";
  const textColor =
    pct >= 80
      ? "text-emerald-400"
      : pct >= 50
        ? "text-amber-400"
        : "text-rose-400";

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-3 mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium uppercase tracking-widest text-[var(--text-primary)]">
          Canonical Coverage —{" "}
          <span className="capitalize font-semibold text-slate-300">
            {entityType}
          </span>
        </span>
        <span className={`text-xs font-bold tabular-nums ${textColor}`}>
          {covered.length}/{canonicalFields.length} ({pct}%)
        </span>
      </div>

      <div className="w-full h-1.5 rounded-full bg-white/[0.08] mb-3 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {missing.length > 0 && (
        <div>
          <p className="text-[11px] text-[var(--text-primary)] mb-1.5 flex items-center gap-1.5">
            <AlertTriangle size={10} className="text-amber-400 shrink-0" />
            <span>
              {missing.length} canonical field{missing.length !== 1 ? "s" : ""}{" "}
              not found in discovered mapping:
            </span>
          </p>
          <div className="flex flex-col gap-1.5">
            {missing.map((cf) => (
              <div
                key={cf}
                className="flex items-center justify-between gap-2 flex-wrap"
              >
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 font-mono">
                  {cf}
                </span>
                {onManualMap && sourceColumns && sourceColumns.length > 0 && (
                  <select
                    className="text-[11px] rounded-lg px-2 py-1 outline-none bg-white/[0.08] border border-[var(--border-muted)] text-[var(--text-primary)] max-w-[220px]"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) onManualMap(cf, e.target.value);
                    }}
                  >
                    <option value="">Map from source column…</option>
                    {sourceColumns.map((sc) => (
                      <option key={sc} value={sc}>
                        {sc}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {missing.length === 0 && (
        <p className="text-[11px] text-emerald-400 flex items-center gap-1">
          <Check size={10} className="shrink-0" />
          All canonical fields for this entity type are present in the mapping.
        </p>
      )}
    </div>
  );
}

// ─── Confirm Mapping Overlay (P3: accepts canonicalFields prop) ───────────────

function ConfirmMappingOverlay({
  entityType,
  canonicalFields,
  fields,
  excluded,
  overrides,
  sourceCount,
  onConfirm,
  onCancel,
  confirming,
}: {
  entityType: EntityType;
  canonicalFields: string[]; // P3: from API
  fields: MdmFieldMapping[];
  excluded: Set<string>;
  overrides: Record<string, string>;
  sourceCount: number;
  onConfirm: () => void;
  onCancel: () => void;
  confirming?: boolean;
}) {
  const includedFields = fields.filter((f) => !excluded.has(f.canonical_field));
  const includedCanonical = new Set(
    includedFields.map((f) => f.canonical_field),
  );
  const coveredCount = canonicalFields.filter((cf) =>
    includedCanonical.has(cf),
  ).length;
  const coveragePct =
    canonicalFields.length > 0
      ? Math.round((coveredCount / canonicalFields.length) * 100)
      : 100;
  const overrideCount = Object.keys(overrides).length;
  const excludedCount = excluded.size;

  const coverageColor =
    coveragePct >= 80
      ? "text-emerald-400"
      : coveragePct >= 50
        ? "text-amber-400"
        : "text-rose-400";

  return (
    <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-[var(--surface-dark)] rounded-2xl border border-[var(--border-muted)] p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-primary)]">
              Confirm Field Mapping
            </h3>
            <p className="text-xs text-[var(--text-primary)] mt-0.5">
              Review the mapping summary before proceeding to match
              configuration.
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={confirming}
            className="text-[var(--text-primary)] hover:text-slate-300 transition-colors ml-4 shrink-0 disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          {[
            {
              label: "Entity Type",
              value: (
                <span className="capitalize font-semibold text-[var(--text-primary)]">
                  {entityType}
                </span>
              ),
            },
            {
              label: "Source Count",
              value: (
                <span className="tabular-nums font-semibold text-[var(--text-primary)]">
                  {sourceCount}
                </span>
              ),
            },
            {
              label: "Fields Included",
              value: (
                <span className="tabular-nums font-semibold text-[var(--text-primary)]">
                  {includedFields.length}
                </span>
              ),
            },
            {
              label: "Fields Excluded",
              value: (
                <span
                  className={`tabular-nums font-semibold ${excludedCount > 0 ? "text-amber-400" : "text-[var(--text-primary)]"}`}
                >
                  {excludedCount}
                </span>
              ),
            },
            {
              label: "AI Overrides",
              value: (
                <span className="tabular-nums font-semibold text-[var(--text-primary)]">
                  {overrideCount}
                </span>
              ),
            },
            {
              label: "Canonical Coverage",
              value: (
                <span className={`tabular-nums font-semibold ${coverageColor}`}>
                  {coveragePct}%
                </span>
              ),
            },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="rounded-xl border border-white/[0.07] bg-white/[0.03] px-3 py-2.5"
            >
              <p className="text-[11px] text-[var(--text-primary)] mb-0.5">
                {label}
              </p>
              <div className="text-sm">{value}</div>
            </div>
          ))}
        </div>

        {coveragePct < 50 && canonicalFields.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-200 mb-5">
            <AlertTriangle
              size={13}
              className="mt-0.5 shrink-0 text-amber-400"
            />
            <span>
              Only {coveragePct}% of the canonical fields for{" "}
              <strong className="capitalize">{entityType}</strong> are mapped.
              Consider adjusting overrides before proceeding.
            </span>
          </div>
        )}

        <div className="flex items-center gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={confirming}
            className="px-4 py-2 rounded-xl text-sm border border-white/[0.10] bg-white/[0.04] text-[var(--text-primary)] hover:bg-white/[0.07] transition-colors disabled:opacity-50"
          >
            Back
          </button>
          <button
            onClick={onConfirm}
            disabled={confirming}
            data-testid="mdm-confirm-mapping"
            className={`flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-medium ${G.bg} text-[#0e0e18] hover:opacity-90 shadow-md transition-opacity disabled:opacity-60`}
          >
            {confirming ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Check size={14} />
                Confirm &amp; Continue
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 3: Entity Type + Attribute Mapping (P1: entity type moved here) ────

function Step3({
  fields,
  loading,
  overrides,
  onOverrideChange,
  onExclude,
  excluded,
  entityType,
  onEntityChange,
  canonicalFields,
  onAiSuggest,
  aiSuggesting,
  sourceColumns,
  onManualMap,
}: {
  fields: MdmFieldMapping[];
  loading: boolean;
  overrides: Record<string, string>;
  onOverrideChange: (canonical: string, value: string) => void;
  onExclude: (canonical: string) => void;
  excluded: Set<string>;
  entityType: EntityType | null;
  onEntityChange: (v: EntityType) => void; // P1
  canonicalFields: string[]; // P3: from API
  onAiSuggest?: () => void;
  aiSuggesting?: boolean;
  sourceColumns?: string[];
  onManualMap?: (canonical: string, source: string) => void;
}) {
  const [expandedAuto, setExpandedAuto] = useState(false);
  const reviewFields = fields.filter((f) => f.needs_review);
  const autoFields = fields.filter((f) => !f.needs_review);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Loader2 size={24} className={`animate-spin ${G.text}`} />
        <p className="text-sm text-[var(--text-primary)]">
          Normalizing source data and discovering field mappings…
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* P1: Entity type picker at top of Step 3 */}
      <EntityTypePicker value={entityType} onChange={onEntityChange} />

      {/* P3: Canonical coverage — uses API-sourced field names */}
      {entityType && (
        <CanonicalCoverage
          entityType={entityType}
          canonicalFields={canonicalFields}
          fields={fields}
          excluded={excluded}
          sourceColumns={sourceColumns}
          onManualMap={onManualMap}
        />
      )}

      {/* AI Suggest Mappings */}
      {onAiSuggest && (
        <div className="flex justify-end">
          <button
            onClick={onAiSuggest}
            disabled={aiSuggesting || loading || !entityType}
            title={!entityType ? "Select an entity type first" : undefined}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all ${
              aiSuggesting || loading || !entityType
                ? "border-[var(--border-muted)] bg-white/[0.04] text-[var(--text-primary)] cursor-not-allowed"
                : `${G.border} ${G.bgFaint} ${G.text} ${G.bgHover} ${G.borderHover}`
            }`}
          >
            {aiSuggesting ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Sparkles size={12} />
            )}
            {aiSuggesting ? "Suggesting…" : "AI Suggest Mappings"}
          </button>
        </div>
      )}

      {reviewFields.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={13} className="text-amber-400" />
            <span className="text-xs font-medium uppercase tracking-widest text-amber-400">
              Needs Review ({reviewFields.length})
            </span>
          </div>
          <div className="flex flex-col gap-2">
            {reviewFields.map((f) => {
              const isExcluded = excluded.has(f.canonical_field);
              return (
                <div
                  key={`${f.canonical_field}:${f.source_system}`}
                  className={`rounded-xl px-4 py-3 border transition-opacity ${
                    isExcluded
                      ? "bg-white/[0.03] border-white/[0.07] opacity-45"
                      : "bg-amber-500/[0.07] border-amber-500/20"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-sm font-medium ${isExcluded ? "text-[var(--text-primary)]" : "text-[var(--text-primary)]"}`}
                        >
                          {f.canonical_field}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded bg-white/[0.07] text-[var(--text-primary)]">
                          {f.source_system}
                        </span>
                        <span className="text-xs tabular-nums text-[var(--text-primary)]">
                          null {(f.null_rate * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div className="text-xs mt-0.5 text-[var(--text-primary)]">
                        Mapped from:{" "}
                        <code className="font-mono text-[var(--text-primary)]">
                          {f.source_attribute}
                        </code>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {!isExcluded && f.alternatives.length > 0 && (
                        <select
                          className={`text-xs rounded-lg px-2 py-1.5 outline-none bg-white/[0.08] border border-[var(--border-muted)] text-[var(--text-primary)] focus:ring-1 ${G.ring}`}
                          value={
                            overrides[f.canonical_field] ?? f.source_attribute
                          }
                          onChange={(e) =>
                            onOverrideChange(f.canonical_field, e.target.value)
                          }
                        >
                          <option value={f.source_attribute}>
                            {f.source_attribute} (default)
                          </option>
                          {f.alternatives.map((alt) => (
                            <option key={alt} value={alt}>
                              {alt}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        onClick={() => onExclude(f.canonical_field)}
                        className={`text-xs px-2 py-1.5 rounded-lg transition-colors border ${
                          isExcluded
                            ? `${G.bgFaint} ${G.border} ${G.text}`
                            : "bg-white/[0.06] border-[var(--border-muted)] text-[var(--text-primary)] hover:text-slate-200"
                        }`}
                      >
                        {isExcluded ? "Include" : "Exclude"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {autoFields.length > 0 && (
        <div>
          <button
            className="flex items-center gap-2 mb-3 w-full text-left"
            onClick={() => setExpandedAuto((o) => !o)}
          >
            <span className="text-xs font-medium uppercase tracking-widest text-[var(--text-primary)]">
              Auto-mapped ({autoFields.length})
            </span>
            {expandedAuto ? (
              <ChevronUp size={12} className="text-[var(--text-primary)]" />
            ) : (
              <ChevronDown size={12} className="text-[var(--text-primary)]" />
            )}
          </button>

          {expandedAuto && (
            <div className="flex flex-col gap-1.5">
              {autoFields.map((f) => (
                <div
                  key={`${f.canonical_field}:${f.source_system}`}
                  className="rounded-xl px-4 py-2.5 flex items-center justify-between gap-4 bg-white/[0.03] border border-white/[0.05] opacity-50"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-medium truncate text-[var(--text-primary)]">
                      {f.canonical_field}
                    </span>
                    <span className="text-xs font-mono truncate text-[var(--text-primary)]">
                      {f.source_attribute}
                    </span>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded shrink-0 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                    Auto-mapped
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {fields.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <p className="text-sm text-[var(--text-primary)]">
            No field mappings returned.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

export default function MdmSourceSelectionView({
  projectId,
  onComplete,
  onBack,
  onViewGoldenRecords,
}: Props) {
  const [step, setStep] = useState(1);

  const { aliases, setAlias } = useConnectorAliases();

  // Step 1 — name only (P1: entity type removed from Step 1)
  const [runName, setRunName] = useState("");

  // Step 2 — P6: single source selection
  const [connectors, setConnectors] = useState<ConnectorCard[]>([]);
  const [connectorsLoading, setConnectorsLoading] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<string | null>(
    null,
  );
  const [apiPresets, setApiPresets] = useState<ConnectionPreset[]>([]);
  const [preIntakeData, setPreIntakeData] = useState<
    Record<
      string,
      { record_count: number; fields: { name: string; null_rate: number }[] }
    >
  >({});
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem("mdm_onboarding_dismissed") === "1",
  );
  const [creatingSample, setCreatingSample] = useState(false);
  const { setEntityType: storeSetEntityType } = useMdmRunStore();
  const [excelSources, setExcelSources] = useState<ConnectorCard[]>([]);
  const [showDbConnect, setShowDbConnect] = useState(false);
  const [presetForConnect, setPresetForConnect] = useState<
    ConnectionPreset | undefined
  >();

  // Additive entry point: reuse a connection ANY agent saved for this
  // project (see admin/project_db_config.list_connections_for_project).
  // Kept alongside the existing DatabaseConnect modal above rather than
  // replacing it — that modal's connection-preset quick-fill (below) is a
  // separate, real feature the generic dialog doesn't replicate.
  const [showSharedConnectDialog, setShowSharedConnectDialog] = useState(false);
  const [sharedDialogTableOrQuery, setSharedDialogTableOrQuery] = useState("");

  // Step 3 — P1: entity type lives here now
  const [entityType, setEntityType] = useState<EntityType | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [fields, setFields] = useState<MdmFieldMapping[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [showConfirmOverlay, setShowConfirmOverlay] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [approving, setApproving] = useState(false);

  // P3: canonical field names fetched from backend /entity-types
  const [canonicalFieldMap, setCanonicalFieldMap] = useState<
    Record<string, string[]>
  >({});

  // Shared
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // P3: Fetch canonical field names from /entity-types on mount
  useEffect(() => {
    getEntityTypes()
      .then((defs) => {
        setCanonicalFieldMap(
          Object.fromEntries(
            defs.map((d) => [
              d.type,
              d.canonical_fields.map((f) => f.field_name),
            ]),
          ),
        );
      })
      .catch(() => {
        // Non-fatal — CanonicalCoverage will show 0 fields until resolved
      });
  }, []);

  // Load connectors when entering Step 2
  useEffect(() => {
    if (step !== 2) return;
    setConnectorsLoading(true);
    Promise.all([
      getConnectors(projectId),
      getConnectionPresets().catch(() => []),
    ])
      .then(([loaded, presets]) => {
        setConnectors(loaded);
        setApiPresets(presets);
        setError(null);
        if (
          loaded.length === 0 &&
          presets.length === 0 &&
          CONNECTION_PRESETS.length === 0
        )
          setShowDbConnect(true);
      })
      .catch((e) => setError(e.message))
      .finally(() => setConnectorsLoading(false));
  }, [step, projectId]);

  // P6: Single-source radio select — deselects previous, fires profiling
  const handleSelectConnector = useCallback((id: string) => {
    setSelectedConnector((prev) => {
      if (prev === id) return null; // clicking selected → deselect
      // Clear pre-intake data for previous selection
      if (prev) {
        setPreIntakeData((d) => {
          const n = { ...d };
          delete n[prev];
          return n;
        });
      }
      // Fire pre-intake profiling for new selection
      profileConnector(id)
        .then((profile) => setPreIntakeData((p) => ({ ...p, [id]: profile })))
        .catch(() => {});
      return id;
    });
  }, []);

  const handleSharedDialogConnectNew = useCallback(
    async (values: DbConnectFormValues): Promise<ConnectResult> => {
      try {
        const { connectDatabase: mdmConnect } =
          await import("../api/mdm-client");
        const card = await mdmConnect({
          ...values,
          port: parseInt(values.port, 10),
          table_or_query: sharedDialogTableOrQuery,
          project_id: projectId,
        });
        setConnectors((prev) => [...prev, card as ConnectorCard]);
        handleSelectConnector(card.id);
        setShowDbConnect(false);
        setPresetForConnect(undefined);
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [sharedDialogTableOrQuery, projectId, handleSelectConnector],
  );

  const handleSharedDialogConnectExisting = useCallback(
    async (connectionId: string): Promise<ConnectResult> => {
      try {
        const { connectDatabase: mdmConnect } =
          await import("../api/mdm-client");
        const card = await mdmConnect({
          connection_id: connectionId,
          table_or_query: sharedDialogTableOrQuery,
          project_id: projectId,
        });
        setConnectors((prev) => [...prev, card as ConnectorCard]);
        handleSelectConnector(card.id);
        setShowDbConnect(false);
        setPresetForConnect(undefined);
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [sharedDialogTableOrQuery, projectId, handleSelectConnector],
  );

  const handleRemoveConnector = useCallback(
    async (id: string) => {
      await deleteConnector(id);
      setConnectors((prev) => prev.filter((c) => c.id !== id));
      setExcelSources((prev) => prev.filter((c) => c.id !== id));
      if (selectedConnector === id) setSelectedConnector(null);
    },
    [selectedConnector],
  );

  const handleOverrideChange = useCallback(
    (canonical: string, value: string) => {
      setOverrides((prev) => ({ ...prev, [canonical]: value }));
    },
    [],
  );

  const handleExclude = useCallback((canonical: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(canonical)) next.delete(canonical);
      else next.add(canonical);
      return next;
    });
  }, []);

  const handleConnectPreset = useCallback((preset: ConnectionPreset) => {
    setPresetForConnect(preset);
    setShowDbConnect(true);
    setError(null);
  }, []);

  // Full source schema (not just fields discovery matched) — lets a canonical
  // field with zero discovered rows still be mapped by hand or by AI Suggest.
  const sourceColumns = useMemo(
    () =>
      selectedConnector && preIntakeData[selectedConnector]
        ? preIntakeData[selectedConnector].fields.map((f) => f.name)
        : [],
    [selectedConnector, preIntakeData],
  );

  const buildManualFieldRow = useCallback(
    (
      canonicalField: string,
      sourceAttribute: string,
      // Present only when this row comes from a backend suggestion — honors
      // its actual auto_mapped/needs_review/confidence instead of blindly
      // forcing every newly-filled field into Needs Review. Without this, a
      // confident synonym/fuzzy match from AI Suggest (e.g. "zip" -> zip_code
      // at 0.95, or "license_nbr" -> license_number at 0.88 — both already
      // classified auto_mapped=true by /suggest-field-mappings) still showed
      // up as "needs review" for no reason, which read as AI Suggest failing
      // to auto-map obvious fields when it actually had.
      suggestion?: {
        confidence: number;
        autoMapped: boolean;
        needsReview: boolean;
      },
    ): MdmFieldMapping => {
      const connector = [...connectors, ...excelSources].find(
        (c) => c.id === selectedConnector,
      );
      const profileField = selectedConnector
        ? preIntakeData[selectedConnector]?.fields.find(
            (f) => f.name === sourceAttribute,
          )
        : undefined;
      return {
        canonical_field: canonicalField,
        source_attribute: sourceAttribute,
        source_system: connector?.name ?? selectedConnector ?? "source",
        auto_mapped: suggestion?.autoMapped ?? false,
        confidence:
          suggestion?.confidence ??
          (profileField ? Math.max(0, 1 - profileField.null_rate) : 0.5),
        // A pure manual pick (no suggestion) has no confidence signal at all,
        // so it always stays flagged for review.
        needs_review: suggestion ? suggestion.needsReview : true,
        alternatives: sourceColumns.filter((c) => c !== sourceAttribute),
        null_rate: profileField?.null_rate ?? 0,
      };
    },
    [connectors, excelSources, selectedConnector, preIntakeData, sourceColumns],
  );

  // Manually map a canonical field that has no discovered row — replaces any
  // existing row for that canonical field and un-excludes it if needed.
  const handleManualMap = useCallback(
    (canonicalField: string, sourceAttribute: string) => {
      setFields((prev) => [
        ...prev.filter((f) => f.canonical_field !== canonicalField),
        buildManualFieldRow(canonicalField, sourceAttribute),
      ]);
      setExcluded((prev) => {
        if (!prev.has(canonicalField)) return prev;
        const next = new Set(prev);
        next.delete(canonicalField);
        return next;
      });
    },
    [buildManualFieldRow],
  );

  const handleAiSuggest = useCallback(async () => {
    if (!entityType || fields.length === 0) return;
    setAiSuggesting(true);
    try {
      const sourceFields = [
        ...new Set([
          ...fields.map((f) => f.source_attribute),
          ...sourceColumns,
        ]),
      ];
      const { mappings } = await suggestFieldMappings(entityType, sourceFields);

      // Canonical fields that already have a live (non-excluded) row — for
      // these, AI Suggest only offers a better source-column choice via the
      // existing per-row override dropdown, same as before.
      const covered = new Set(
        fields
          .filter((f) => !excluded.has(f.canonical_field))
          .map((f) => f.canonical_field),
      );

      // Canonical fields with zero discovered rows — AI Suggest can now fill
      // these in directly (this is the gap that made the button a no-op for
      // anything the Canonical Coverage panel listed as missing).
      const newRows: MdmFieldMapping[] = [];
      const claimed = new Set<string>();
      mappings.forEach((s) => {
        if (!s.canonical_field || s.confidence < 0.5) return;
        if (covered.has(s.canonical_field)) return;
        if (claimed.has(s.canonical_field)) return; // first/best suggestion wins
        claimed.add(s.canonical_field);
        newRows.push(
          buildManualFieldRow(s.canonical_field, s.source_attribute, {
            confidence: s.confidence,
            autoMapped: s.auto_mapped,
            needsReview: s.needs_review,
          }),
        );
      });
      if (newRows.length > 0) {
        // A field can be "not covered" because it's excluded rather than
        // absent — replace its row instead of appending a duplicate, and
        // un-exclude it since the user is actively re-mapping it.
        const newCanonical = new Set(newRows.map((r) => r.canonical_field));
        setFields((prev) => [
          ...prev.filter((f) => !newCanonical.has(f.canonical_field)),
          ...newRows,
        ]);
        setExcluded((prev) => {
          if (![...newCanonical].some((cf) => prev.has(cf))) return prev;
          const next = new Set(prev);
          newCanonical.forEach((cf) => next.delete(cf));
          return next;
        });
      }

      setOverrides((prev) => {
        const next = { ...prev };
        mappings.forEach((s) => {
          if (
            s.canonical_field &&
            s.confidence >= 0.5 &&
            covered.has(s.canonical_field) &&
            !prev[s.canonical_field]
          ) {
            next[s.canonical_field] = s.source_attribute;
          }
        });
        return next;
      });
    } catch {
      // AI suggest is best-effort — silently ignore errors
    } finally {
      setAiSuggesting(false);
    }
  }, [entityType, fields, excluded, sourceColumns, buildManualFieldRow]);

  // Run the same classification AI Suggest does automatically the moment an
  // entity type is picked, instead of requiring a manual click. Without
  // this, initial Canonical Coverage only counted a field as covered when
  // the raw source column name was byte-identical to the canonical name —
  // "zip" vs "zip_code" or "license_nbr" vs "license_number" showed up as
  // missing even though the exact same classifier (once triggered manually)
  // confidently auto-maps both. Guarded by ref so it fires once per entity
  // type selection, not on every fields/excluded change afterward — the
  // user's own subsequent edits must not be silently overwritten.
  const autoSuggestedForRef = useRef<EntityType | null>(null);
  useEffect(() => {
    if (!entityType || fields.length === 0) return;
    if (autoSuggestedForRef.current === entityType) return;
    autoSuggestedForRef.current = entityType;
    handleAiSuggest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, fields.length]);

  const handleViewPastRun = useCallback(
    (rid: string, _name: string) => {
      if (onViewGoldenRecords) onViewGoldenRecords(rid);
    },
    [onViewGoldenRecords],
  );

  const goToStep2 = () => {
    if (!runName.trim()) {
      setError("Please enter a run name.");
      return;
    }
    setError(null);
    setStep(2);
  };

  const goToStep3 = async () => {
    if (!selectedConnector) {
      setError("Select a source before continuing.");
      return;
    }
    setError(null);
    setSubmitting(true);
    setFieldsLoading(true);
    try {
      // Create run with a placeholder entity type — will be updated from Step 3
      const run = await createRun(runName.trim(), "person", projectId);
      setRunId(run.id);
      const card = [...connectors, ...excelSources].find(
        (c) => c.id === selectedConnector,
      );
      const source = await registerSourceFromConnector(
        run.id,
        selectedConnector,
        card?.name ?? selectedConnector,
      );
      await waitForSourceRegistration(run.id, source.source_id);
      await normalizeRun(run.id);
      await waitForNormalization(run.id);
      const mappings = await getFields(run.id);
      setFields(mappings);
      setStep(3);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to register source.");
    } finally {
      setSubmitting(false);
      setFieldsLoading(false);
    }
  };

  // P2: Persist field mapping overrides + apply entity template before navigating
  const handleApprove = useCallback(async () => {
    if (!runId || !entityType) return;
    setApproving(true);
    setError(null);
    try {
      // 1. Build override list — active (non-excluded) fields with steward-chosen mapping
      const overrideList = fields
        .filter((f) => !excluded.has(f.canonical_field))
        .map((f) => ({
          source_attribute: overrides[f.canonical_field] ?? f.source_attribute,
          canonical_field: f.canonical_field,
        }));

      // 2. Persist to backend (PATCH /runs/{run_id}/field-mappings) — includes
      //    which canonical fields were explicitly excluded, so
      //    apply-entity-template (next) doesn't silently re-derive one of
      //    them via its own exact/synonym fallback.
      if (overrideList.length > 0) {
        await updateFieldMappings(runId, overrideList, Array.from(excluded));
      }

      // 3. Seed the Rule Builder with entity-appropriate match config
      //    (POST /runs/{run_id}/apply-entity-template)
      await applyEntityTemplate(runId, entityType);

      // 4. Navigate to Rule Builder
      storeSetEntityType(entityType);
      onComplete(runId, entityType);
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? `Failed to save mapping: ${e.message}`
          : "Failed to save field mappings. Please try again.",
      );
      setApproving(false);
    }
  }, [
    runId,
    entityType,
    fields,
    excluded,
    overrides,
    storeSetEntityType,
    onComplete,
  ]);

  // P3: Canonical fields for the currently selected entity type
  const currentCanonicalFields = entityType
    ? (canonicalFieldMap[entityType] ?? [])
    : [];

  // P6: Pre-intake fields from single selected connector
  const preIntakeFields: MdmFieldMapping[] =
    selectedConnector && preIntakeData[selectedConnector]
      ? preIntakeData[selectedConnector].fields.map((f) => {
          const connector = [...connectors, ...excelSources].find(
            (c) => c.id === selectedConnector,
          );
          return {
            canonical_field: f.name,
            source_attribute: f.name,
            source_system: connector?.name ?? selectedConnector,
            auto_mapped: true,
            confidence: Math.max(0, 1 - f.null_rate),
            needs_review: f.null_rate > 0.2,
            alternatives: [],
            null_rate: f.null_rate,
          };
        })
      : [];

  const canNext1 = runName.trim().length > 0; // P1: name only
  const canNext2 = selectedConnector !== null; // P6: single source
  const canApprove = !fieldsLoading && !submitting && !!entityType; // P1: entity type required

  return (
    <div className="min-h-screen flex items-start justify-center p-6 bg-[var(--surface-dark)]">
      <div className="w-full max-w-2xl rounded-2xl p-8 mt-12 border border-[var(--border-muted)] bg-white/[0.04] backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`text-xs uppercase tracking-widest font-medium ${G.text}`}
              >
                Master Data Management
              </span>
            </div>
            <h1 className="text-lg font-semibold text-[var(--text-primary)]">
              New Resolution Run
            </h1>
          </div>
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors hover:bg-[var(--overlay-strong)] text-[var(--text-primary)] border border-white/[0.08]"
          >
            <ChevronLeft size={13} />
            Back
          </button>
        </div>

        <StepIndicator current={step} />

        {error && (
          <ErrorBanner message={error} onDismiss={() => setError(null)} />
        )}

        {/* Step 1: Configure — name only */}
        {step === 1 && (
          <Step1
            runName={runName}
            onNameChange={setRunName}
            projectId={projectId}
            onViewPastRun={handleViewPastRun}
          />
        )}

        {/* Step 2: Single source selection */}
        {step === 2 && (
          <>
            {/* First-run onboarding coach mark */}
            {!onboardingDismissed &&
              connectors.length === 0 &&
              !connectorsLoading && (
                <div className="flex items-start gap-3 rounded-xl border border-indigo-500/30 bg-indigo-500/10 px-5 py-4 text-sm text-indigo-200 mb-4">
                  <Sparkles
                    size={16}
                    className="mt-0.5 shrink-0 text-indigo-400"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-indigo-100">New to MDM?</p>
                    <p className="text-xs text-indigo-300 mt-0.5">
                      Try a pre-loaded sample dataset to explore the full
                      workflow before connecting your real data.
                    </p>
                    <button
                      disabled={creatingSample}
                      onClick={async () => {
                        setCreatingSample(true);
                        try {
                          const { run_id } = await createSampleRun(projectId);
                          const sampleRun = await getRunStatus(run_id).catch(
                            () => null,
                          );
                          const sampleEntityType: EntityType =
                            sampleRun?.entity_type ?? "person";
                          storeSetEntityType(sampleEntityType);
                          onComplete(run_id, sampleEntityType);
                        } finally {
                          setCreatingSample(false);
                        }
                      }}
                      className="mt-2 rounded-lg border border-indigo-400/40 bg-indigo-400/20 px-3 py-1.5 text-xs font-semibold text-indigo-200 transition hover:bg-indigo-400/30 disabled:opacity-50"
                    >
                      {creatingSample ? "Creating sample…" : "Load Sample Data"}
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      localStorage.setItem("mdm_onboarding_dismissed", "1");
                      setOnboardingDismissed(true);
                    }}
                    className="text-indigo-400/60 hover:text-indigo-300"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

            <Step2
              projectId={projectId}
              connectors={connectors}
              selected={selectedConnector}
              loading={connectorsLoading}
              onSelect={handleSelectConnector}
              excelSources={excelSources}
              onExcelUploaded={(card) =>
                setExcelSources((prev) => [...prev, card])
              }
              onConnectDatabase={() => {
                setPresetForConnect(undefined);
                setShowDbConnect(true);
                setError(null);
              }}
              onConnectPreset={handleConnectPreset}
              aliases={aliases}
              onAliasChange={setAlias}
              onRemove={handleRemoveConnector}
              apiPresets={apiPresets}
            />
            <div className="flex justify-center -mt-4">
              <button
                type="button"
                data-testid="mdm-open-saved-connections"
                onClick={() => {
                  setSharedDialogTableOrQuery("");
                  setShowSharedConnectDialog(true);
                }}
                className="text-[11px] text-white/40 hover:text-white/60 transition-colors underline"
              >
                Or pick a connection saved by another agent…
              </button>
            </div>
            <PreIntakePanel
              connectors={[...connectors, ...excelSources]}
              selected={selectedConnector}
              fields={preIntakeFields}
              aliases={aliases}
            />
          </>
        )}

        {/* DB Connect modal */}
        {showDbConnect && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
            <div className="w-full max-w-3xl bg-[var(--surface-dark)] rounded-2xl border border-[var(--border-muted)] overflow-auto max-h-[90vh]">
              <div className="flex justify-center pt-4 -mb-4">
                <button
                  type="button"
                  data-testid="mdm-open-saved-connections-modal"
                  onClick={() => {
                    setSharedDialogTableOrQuery("");
                    setShowSharedConnectDialog(true);
                  }}
                  className="text-[11px] text-white/40 hover:text-white/60 transition-colors underline"
                >
                  Or pick a connection saved by another agent…
                </button>
              </div>
              <DatabaseConnect
                mode="mdm"
                onBack={() => {
                  setShowDbConnect(false);
                  setPresetForConnect(undefined);
                }}
                onComplete={() => {}}
                initialValues={
                  presetForConnect
                    ? {
                        db_type: presetForConnect.db_type,
                        host: presetForConnect.host,
                        port: presetForConnect.port,
                        database: presetForConnect.database,
                        schema_name: presetForConnect.schema_name,
                        username: presetForConnect.username,
                        table_or_query: presetForConnect.table_or_query,
                        account: presetForConnect.account,
                        warehouse: presetForConnect.warehouse,
                        role: presetForConnect.role,
                      }
                    : undefined
                }
                onMdmConnectorCreated={(card) => {
                  const enriched: ConnectorCard = {
                    ...card,
                    alias: presetForConnect?.alias,
                  };
                  setConnectors((prev) => [...prev, enriched]);
                  handleSelectConnector(card.id);
                  setShowDbConnect(false);
                  setPresetForConnect(undefined);
                }}
              />
            </div>
          </div>
        )}

        {/* Step 3: Entity Type + Field Mapping */}
        {step === 3 && (
          <>
            <Step3
              fields={fields}
              loading={fieldsLoading}
              overrides={overrides}
              onOverrideChange={handleOverrideChange}
              onExclude={handleExclude}
              excluded={excluded}
              entityType={entityType}
              onEntityChange={setEntityType}
              canonicalFields={currentCanonicalFields}
              onAiSuggest={handleAiSuggest}
              aiSuggesting={aiSuggesting}
              sourceColumns={sourceColumns}
              onManualMap={handleManualMap}
            />
            <PreIntakePanel
              connectors={[...connectors, ...excelSources]}
              selected={selectedConnector}
              fields={fields}
              aliases={aliases}
            />
          </>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/[0.07]">
          <button
            onClick={() => {
              setError(null);
              if (step > 1) setStep((s) => s - 1);
              else onBack();
            }}
            className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-xl transition-colors hover:bg-[var(--overlay-strong)] text-[var(--text-primary)] border border-white/[0.08] disabled:opacity-40"
            disabled={submitting || approving}
          >
            <ChevronLeft size={15} />
            {step === 1 ? "Cancel" : "Back"}
          </button>

          {step === 1 && (
            <button
              onClick={goToStep2}
              disabled={!canNext1}
              data-testid="mdm-wizard-next-1"
              className={`flex items-center gap-1.5 text-sm px-5 py-2 rounded-xl font-medium transition-all duration-150 ${
                canNext1
                  ? `${G.bg} text-[#0e0e18] hover:opacity-90 shadow-md`
                  : "bg-white/[0.07] text-[var(--text-primary)] cursor-not-allowed"
              }`}
            >
              Next
              <ChevronRight size={15} />
            </button>
          )}

          {step === 2 && (
            <button
              onClick={goToStep3}
              disabled={!canNext2 || submitting}
              data-testid="mdm-wizard-next-2"
              className={`flex items-center gap-1.5 text-sm px-5 py-2 rounded-xl font-medium transition-all duration-150 ${
                canNext2 && !submitting
                  ? `${G.bg} text-[#0e0e18] hover:opacity-90 shadow-md`
                  : "bg-white/[0.07] text-[var(--text-primary)] cursor-not-allowed"
              }`}
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Registering…
                </>
              ) : (
                <>
                  Next
                  <ChevronRight size={15} />
                </>
              )}
            </button>
          )}

          {step === 3 && (
            <button
              onClick={() => {
                if (canApprove) setShowConfirmOverlay(true);
              }}
              disabled={!canApprove}
              data-testid="mdm-wizard-approve"
              className={`flex items-center gap-1.5 text-sm px-5 py-2 rounded-xl font-medium transition-all duration-150 ${
                canApprove
                  ? `${G.bg} text-[#0e0e18] hover:opacity-90 shadow-md`
                  : "bg-white/[0.07] text-[var(--text-primary)] cursor-not-allowed"
              }`}
            >
              {fieldsLoading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Loading…
                </>
              ) : !entityType ? (
                <>Select entity type</>
              ) : (
                <>
                  <Check size={14} />
                  Approve &amp; Continue
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* P2+P3: Confirm Mapping overlay */}
      {showConfirmOverlay && entityType && (
        <ConfirmMappingOverlay
          entityType={entityType}
          canonicalFields={currentCanonicalFields}
          fields={fields}
          excluded={excluded}
          overrides={overrides}
          sourceCount={selectedConnector ? 1 : 0}
          confirming={approving}
          onConfirm={() => {
            setShowConfirmOverlay(false);
            handleApprove();
          }}
          onCancel={() => setShowConfirmOverlay(false)}
        />
      )}
      <ConnectDatabaseDialog
        open={showSharedConnectDialog}
        onClose={() => setShowSharedConnectDialog(false)}
        title="Connect a Saved Database"
        projectId={projectId}
        agentKey="mdm"
        extraField={{
          key: "table_or_query",
          label: "Table or Query",
          value: sharedDialogTableOrQuery,
          onChange: setSharedDialogTableOrQuery,
          placeholder: "e.g. worker_personal_info",
          required: true,
        }}
        testIdPrefix="mdm-saved"
        onConnectExisting={handleSharedDialogConnectExisting}
        onConnectNew={handleSharedDialogConnectNew}
      />
    </div>
  );
}
